// The dynamic request form and the two places it is used: requesting a new document
// (RequestFlowModal) and answering a correction on an existing request (ResubmitPanel).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { SafeAreaView } from 'react-native-safe-area-context';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { ThemeColors } from '../../context/ThemeContext';
import { documentsAPI } from '../../services/api';
import {
  CatalogItem,
  DocumentForm,
  FormField,
  ParentRequestDetail,
  ParentRequestSummary,
  PickedDocumentFile,
} from '../../types';
import { BorderRadius, FontSize, Spacing } from '../../theme/colors';
import {
  FormFiles,
  FormValues,
  INVALID_FORM_CODE,
  STALE_FORM_CODE,
  buildSubmissionFiles,
  buildSubmissionValues,
  displayAnswer,
  extractDocumentError,
  fileRule,
  formatDocDate,
  formatFee,
  hasFee,
  hasTurnaround,
  formatFileSize,
  formatProcessingDays,
  initialFormValues,
  isFieldVisible,
  isReadOnlyField,
  isTruthy,
  mapServerFieldErrors,
  requestStatusIcon,
  validateDocumentForm,
  validatePickedFile,
} from '../../utils/documents';
import { Logger } from '../../utils/logger';
import { DocDatePicker, ErrorBanner, InfoBanner, OptionSheet, StatusPill, useDocStyles } from './DocumentUi';
import { pickDocumentFile } from './documentFiles';

// A plain string as a JSX tag renders that DOM element on web — used only behind
// Platform.OS === 'web' to get the browser's own date picker (EarlyPickupScreen.web does the same).
const WebInput: any = 'input';

const DEFAULT_TEXT_MAX = 500;
const DEFAULT_TEXTAREA_MAX = 2000;
const NOTE_MAX = 1000;
const FIX_FIELDS_MESSAGE = 'Some answers need attention. Check the highlighted fields.';

// ── Form state ──────────────────────────────────────────────────────────────

/** State updaters shared by the request flow and the resubmit form. */
function useFieldState(initialValues: FormValues) {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [files, setFiles] = useState<FormFiles>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const onValueChange = useCallback((key: string, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const onFilePicked = useCallback((key: string, file: PickedDocumentFile | null, problem: string | null) => {
    setFiles(prev => {
      const next = { ...prev };
      if (file && !problem) next[key] = file;
      else delete next[key];
      return next;
    });
    setErrors(prev => {
      const next = { ...prev };
      if (problem) next[key] = problem;
      else delete next[key];
      return next;
    });
  }, []);

  const reset = useCallback((nextValues: FormValues) => {
    setValues(nextValues);
    setFiles({});
    setErrors({});
  }, []);

  return { values, files, errors, setErrors, onValueChange, onFilePicked, reset };
}

// ── One field ───────────────────────────────────────────────────────────────

interface DynamicFieldProps {
  field: FormField;
  value: string;
  file?: PickedDocumentFile | null;
  /** Resubmission: the file already on the request for this field. */
  existingFileName?: string | null;
  error?: string;
  onChange: (value: string) => void;
  /** A newly chosen file (or null to remove it), and a problem with it if any. */
  onFilePicked: (file: PickedDocumentFile | null, problem: string | null) => void;
}

/** One request-form field, rendered by its type. */
function DynamicField({ field, value, file, existingFileName, error, onChange, onFilePicked }: DynamicFieldProps) {
  const { colors, s } = useDocStyles();
  const styles = useMemo(() => createFieldStyles(colors), [colors]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picking, setPicking] = useState(false);

  const locked = field.isEditable === false;
  const readOnly = isReadOnlyField(field);
  const hasError = !!error;

  const labelRow = (
    <View style={styles.labelRow}>
      <Text style={styles.label} nativeID={`label-${field.key}`}>
        {field.label}
        {field.isRequired && !readOnly ? <Text style={s.required}> *</Text> : null}
      </Text>
      {locked && !readOnly && (
        <View style={styles.lockedTag}>
          <FeatherIcon name="lock" size={10} color={colors.textSecondary} />
          <Text style={styles.lockedTagText}>Not editable</Text>
        </View>
      )}
    </View>
  );

  const help = !!field.helpText && <Text style={s.helperText}>{field.helpText}</Text>;
  const errorText = hasError && (
    <Text style={s.errorText} accessibilityLiveRegion="polite">
      {error}
    </Text>
  );

  // ── Read-only (school's own data) ──
  if (readOnly) {
    return (
      <View style={styles.group}>
        {labelRow}
        <View style={styles.readOnlyBox} accessibilityLabel={`${field.label}: ${field.readOnlyValue || 'Not on file'}`}>
          <FeatherIcon name="lock" size={14} color={colors.textMuted} />
          <Text style={styles.readOnlyText}>{field.readOnlyValue?.trim() || 'Not on file'}</Text>
        </View>
        <Text style={s.helperText}>
          {field.helpText || 'Filled in from school records. Contact the office if it is wrong.'}
        </Text>
      </View>
    );
  }

  // ── Locked on a resubmission: show the earlier answer ──
  if (locked && field.fieldType !== 'FILE') {
    const shown =
      field.fieldType === 'CHECKBOX'
        ? isTruthy(value) ? 'Yes' : 'No'
        : field.fieldType === 'DATE'
          ? formatDocDate(value) || '—'
          : value?.trim() || '—';
    return (
      <View style={styles.group}>
        {labelRow}
        <View style={styles.readOnlyBox}>
          <Text style={styles.readOnlyText}>{shown}</Text>
        </View>
      </View>
    );
  }

  const handlePick = async () => {
    if (locked || picking) return;
    setPicking(true);
    try {
      const picked = await pickDocumentFile(fileRule(field).extensions);
      if (!picked) return;
      onFilePicked(picked, validatePickedFile(field, picked));
    } catch (err) {
      Logger.error('Documents', 'File picker failed', err);
      onFilePicked(null, 'Could not open the file picker. Please try again.');
    } finally {
      setPicking(false);
    }
  };

  switch (field.fieldType) {
    case 'CHECKBOX': {
      const checked = isTruthy(value);
      return (
        <View style={styles.group}>
          <TouchableOpacity
            style={[styles.checkRow, hasError && styles.checkRowError]}
            onPress={() => onChange(checked ? '' : 'true')}
            accessibilityRole="checkbox"
            accessibilityState={{ checked }}
            accessibilityLabel={field.label}>
            <FeatherIcon
              name={checked ? 'check-square' : 'square'}
              size={20}
              color={checked ? colors.primary : colors.textMuted}
            />
            <Text style={styles.checkLabel}>
              {field.label}
              {field.isRequired ? <Text style={s.required}> *</Text> : null}
            </Text>
          </TouchableOpacity>
          {help}
          {errorText}
        </View>
      );
    }

    case 'RADIO':
      return (
        <View style={styles.group} accessibilityRole="radiogroup" accessibilityLabel={field.label}>
          {labelRow}
          {(field.options ?? []).map(option => {
            const selected = option.toLowerCase() === value.trim().toLowerCase();
            return (
              <TouchableOpacity
                key={option}
                style={[styles.radioRow, selected && styles.radioRowActive]}
                onPress={() => onChange(option)}
                accessibilityRole="radio"
                accessibilityState={{ selected }}
                accessibilityLabel={option}>
                <FeatherIcon
                  name={selected ? 'check-circle' : 'circle'}
                  size={18}
                  color={selected ? colors.primary : colors.textMuted}
                />
                <Text style={styles.radioText}>{option}</Text>
              </TouchableOpacity>
            );
          })}
          {help}
          {errorText}
        </View>
      );

    case 'DROPDOWN':
      return (
        <View style={styles.group}>
          {labelRow}
          <TouchableOpacity
            style={[styles.selectBox, hasError && s.inputError]}
            onPress={() => setPickerOpen(true)}
            accessibilityRole="button"
            accessibilityLabel={`${field.label}: ${value || 'not chosen'}. Tap to choose.`}>
            <Text style={[styles.selectText, !value && styles.placeholderText]} numberOfLines={1}>
              {value || field.placeholder || 'Choose an option'}
            </Text>
            <FeatherIcon name="chevron-down" size={18} color={colors.textSecondary} />
          </TouchableOpacity>
          {help}
          {errorText}
          <OptionSheet
            visible={pickerOpen}
            title={field.label}
            options={field.options ?? []}
            selected={value}
            onSelect={onChange}
            onClose={() => setPickerOpen(false)}
          />
        </View>
      );

    case 'DATE':
      return (
        <View style={styles.group}>
          {labelRow}
          {Platform.OS === 'web' ? (
            <WebInput
              type="date"
              value={value}
              aria-label={field.label}
              onChange={(e: any) => onChange(e.target.value)}
              style={{
                border: `${hasError ? 1.5 : 1}px solid ${hasError ? colors.danger : colors.border}`,
                borderRadius: 12,
                padding: 12,
                color: colors.textPrimary,
                backgroundColor: colors.background,
                fontSize: 14,
                fontFamily: 'inherit',
                width: '100%',
                boxSizing: 'border-box',
                outline: 'none',
                colorScheme: 'light dark',
              }}
            />
          ) : (
            <TouchableOpacity
              style={[styles.selectBox, hasError && s.inputError]}
              onPress={() => setPickerOpen(true)}
              accessibilityRole="button"
              accessibilityLabel={`${field.label}: ${value ? formatDocDate(value) : 'not chosen'}. Tap to choose a date.`}>
              <FeatherIcon name="calendar" size={16} color={colors.textSecondary} />
              <Text style={[styles.selectText, !value && styles.placeholderText]}>
                {value ? formatDocDate(value) : field.placeholder || 'Select a date'}
              </Text>
              {!!value && (
                <TouchableOpacity onPress={() => onChange('')} accessibilityLabel="Clear date" hitSlop={8}>
                  <FeatherIcon name="x" size={16} color={colors.textMuted} />
                </TouchableOpacity>
              )}
            </TouchableOpacity>
          )}
          {help}
          {errorText}
          {Platform.OS !== 'web' && (
            <DocDatePicker
              visible={pickerOpen}
              title={field.label}
              value={value}
              onSelect={date => {
                onChange(date);
                setPickerOpen(false);
              }}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </View>
      );

    case 'FILE': {
      const rule = fileRule(field);
      const showExisting = !file && !!existingFileName;
      return (
        <View style={styles.group}>
          {labelRow}
          {file ? (
            <View style={[styles.fileChip, hasError && s.inputError]}>
              <FeatherIcon name="paperclip" size={16} color={colors.primary} />
              <View style={styles.fileChipText}>
                <Text style={styles.fileName} numberOfLines={1}>
                  {file.name}
                </Text>
                {typeof file.size === 'number' && <Text style={styles.fileMeta}>{formatFileSize(file.size)}</Text>}
              </View>
              {!locked && (
                <TouchableOpacity
                  onPress={() => onFilePicked(null, null)}
                  accessibilityRole="button"
                  accessibilityLabel={`Remove ${file.name}`}
                  hitSlop={8}>
                  <FeatherIcon name="x" size={18} color={colors.textSecondary} />
                </TouchableOpacity>
              )}
            </View>
          ) : showExisting ? (
            <View style={styles.fileChip}>
              <FeatherIcon name="file" size={16} color={colors.textSecondary} />
              <View style={styles.fileChipText}>
                <Text style={styles.fileName} numberOfLines={1}>
                  {existingFileName}
                </Text>
                <Text style={styles.fileMeta}>Already attached</Text>
              </View>
            </View>
          ) : null}

          {!locked && (
            <TouchableOpacity
              style={[styles.fileButton, hasError && !file && s.inputError]}
              onPress={handlePick}
              disabled={picking}
              accessibilityRole="button"
              accessibilityLabel={file || showExisting ? `Replace file for ${field.label}` : `Choose file for ${field.label}`}>
              {picking ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <FeatherIcon name="upload" size={16} color={colors.primary} />
              )}
              <Text style={styles.fileButtonText}>{file || showExisting ? 'Replace file' : 'Choose file'}</Text>
            </TouchableOpacity>
          )}
          <Text style={s.helperText}>
            {rule.extensions.map(e => e.toUpperCase()).join(', ')} · up to {rule.maxMb} MB
            {field.helpText ? ` · ${field.helpText}` : ''}
          </Text>
          {errorText}
        </View>
      );
    }

    case 'TEXTAREA':
    case 'TEXT':
    case 'NUMBER':
    default: {
      const isArea = field.fieldType === 'TEXTAREA';
      const isNumber = field.fieldType === 'NUMBER';
      const max = isNumber
        ? undefined
        : field.validation?.maxLength ?? (isArea ? DEFAULT_TEXTAREA_MAX : DEFAULT_TEXT_MAX);
      return (
        <View style={styles.group}>
          {labelRow}
          <TextInput
            style={[s.input, isArea && styles.textArea, hasError && s.inputError]}
            value={value}
            onChangeText={onChange}
            placeholder={field.placeholder ?? undefined}
            placeholderTextColor={colors.textMuted}
            keyboardType={isNumber ? 'decimal-pad' : 'default'}
            multiline={isArea}
            textAlignVertical={isArea ? 'top' : 'center'}
            maxLength={max}
            accessibilityLabel={field.label}
            accessibilityHint={field.helpText ?? undefined}
          />
          {isArea && !!max && (
            <Text style={s.counter}>
              {value.length}/{max}
            </Text>
          )}
          {help}
          {errorText}
        </View>
      );
    }
  }
}

// ── The field list ──────────────────────────────────────────────────────────

interface FieldListProps {
  fields: FormField[];
  values: FormValues;
  files: FormFiles;
  errors: Record<string, string>;
  /** Resubmission: field key to the file name already on the request. */
  existingFiles?: Record<string, string>;
  onValueChange: (key: string, value: string) => void;
  onFilePicked: (key: string, file: PickedDocumentFile | null, problem: string | null) => void;
}

/** The dynamic form: every visible field in order. A hidden field is not rendered at all. */
function DocumentFieldList({ fields, values, files, errors, existingFiles, onValueChange, onFilePicked }: FieldListProps) {
  return (
    <View>
      {fields
        .filter(field => isFieldVisible(field, values))
        .map(field => (
          <DynamicField
            key={field.key}
            field={field}
            value={values[field.key] ?? ''}
            file={files[field.key] ?? null}
            existingFileName={existingFiles?.[field.key] ?? null}
            error={errors[field.key]}
            onChange={value => onValueChange(field.key, value)}
            onFilePicked={(file, problem) => onFilePicked(field.key, file, problem)}
          />
        ))}
    </View>
  );
}

// ── Requesting a document ───────────────────────────────────────────────────

type Step = 'form' | 'review' | 'done';

interface RequestFlowModalProps {
  visible: boolean;
  item: CatalogItem | null;
  studentId: string;
  onClose: () => void;
  /** Called once the school has the request, so lists can refresh. */
  onSubmitted: (request: ParentRequestSummary) => void;
  onViewRequest: (requestId: string) => void;
}

/**
 * Requesting one document: the dynamic form, a review of every answer with the fee and
 * expected turnaround plus a required confirmation, then a success view with the
 * request number. Full screen on phones, a centred dialog on wide web screens.
 */
export function RequestFlowModal({ visible, item, studentId, onClose, onSubmitted, onViewRequest }: RequestFlowModalProps) {
  const { colors, s } = useDocStyles();
  const styles = useMemo(() => createFlowStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isWide = Platform.OS === 'web' && width >= 768;

  const [step, setStep] = useState<Step>('form');
  const [form, setForm] = useState<DocumentForm | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ParentRequestSummary | null>(null);
  const scrollRef = useRef<ScrollView>(null);

  const { values, files, errors, setErrors, onValueChange, onFilePicked, reset } = useFieldState({});

  const documentTypeId = item?.documentTypeId;

  // Read by a stale-form reload without making loadForm change on every keystroke.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const loadForm = useCallback(
    async (keepAnswers: boolean) => {
      if (!documentTypeId || !studentId) return;
      setLoading(true);
      setLoadError(null);
      try {
        const { data } = await documentsAPI.getForm(documentTypeId, studentId);
        setForm(data);
        const fresh = initialFormValues(data.fields);
        if (keepAnswers) {
          // A stale-form reload keeps every answer whose field still exists.
          Object.keys(fresh).forEach(key => {
            const previous = valuesRef.current[key];
            if (previous !== undefined) fresh[key] = previous;
          });
        }
        reset(fresh);
      } catch (err) {
        Logger.error('Documents', 'Failed to load request form', err);
        setForm(null);
        setLoadError(extractDocumentError(err, 'Could not load this form.').message);
      } finally {
        setLoading(false);
      }
    },
    [documentTypeId, studentId, reset],
  );

  // A fresh start for every document and every child: answers never carry across children.
  useEffect(() => {
    if (!visible) return;
    setStep('form');
    setNotice(null);
    setSubmitError(null);
    setConfirmed(false);
    setConfirmError(false);
    setResult(null);
    setForm(null);
    reset({});
    loadForm(false);
  }, [visible, documentTypeId, studentId, loadForm, reset]);

  const scrollTop = () => scrollRef.current?.scrollTo({ y: 0, animated: true });

  const goToReview = () => {
    if (!form) return;
    const problems = validateDocumentForm(form.fields, values, files);
    setErrors(problems);
    setSubmitError(null);
    if (Object.keys(problems).length > 0) {
      setSubmitError(FIX_FIELDS_MESSAGE);
      scrollTop();
      return;
    }
    setStep('review');
    scrollTop();
  };

  const submit = async () => {
    if (!form || submitting) return;
    if (!confirmed) {
      setConfirmError(true);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data } = await documentsAPI.submit({
        studentId: form.studentId || studentId,
        documentTypeId: form.documentTypeId,
        configVersion: form.configVersion,
        confirmed: true,
        values: buildSubmissionValues(form.fields, values),
        files: buildSubmissionFiles(form.fields, values, files),
      });
      setResult(data);
      setStep('done');
      scrollTop();
      onSubmitted(data);
    } catch (err) {
      Logger.error('Documents', 'Failed to submit document request', err);
      const problem = extractDocumentError(err, 'Could not send your request. Please try again.');
      if (problem.code === STALE_FORM_CODE) {
        setNotice(problem.message);
        setStep('form');
        setConfirmed(false);
        await loadForm(true);
      } else if (problem.code === INVALID_FORM_CODE || Object.keys(problem.fieldErrors).length > 0) {
        setErrors(mapServerFieldErrors(problem.fieldErrors, form.fields));
        setSubmitError(problem.message);
        setStep('form');
      } else {
        setSubmitError(problem.message);
      }
      scrollTop();
    } finally {
      setSubmitting(false);
    }
  };

  const eligible = form?.eligibility?.isEligible !== false;
  const title = form?.name ?? item?.name ?? 'Request document';

  const header = (
    <View style={styles.header}>
      <View style={styles.headerText}>
        <Text style={styles.headerTitle} numberOfLines={2} accessibilityRole="header">
          {step === 'done' ? 'Request submitted' : title}
        </Text>
        {!!form && step !== 'done' && (
          <Text style={styles.headerSubtitle} numberOfLines={1}>
            For {form.studentName}
            {form.className ? ` · ${form.className}` : ''}
          </Text>
        )}
      </View>
      <TouchableOpacity
        onPress={onClose}
        disabled={submitting}
        style={styles.closeBtn}
        accessibilityRole="button"
        accessibilityLabel="Close">
        <FeatherIcon name="x" size={22} color={colors.textSecondary} />
      </TouchableOpacity>
    </View>
  );

  const stepper = step !== 'done' && !!form && eligible && (
    <View style={styles.stepper}>
      {(['form', 'review'] as Step[]).map((key, index) => {
        const active = step === key;
        const done = step === 'review' && key === 'form';
        return (
          <View key={key} style={styles.stepItem}>
            <View style={[styles.stepDot, (active || done) && styles.stepDotActive]}>
              {done ? (
                <FeatherIcon name="check" size={12} color="#FFFFFF" />
              ) : (
                <Text style={[styles.stepNum, active && styles.stepNumActive]}>{index + 1}</Text>
              )}
            </View>
            <Text style={[styles.stepLabel, active && styles.stepLabelActive]}>
              {key === 'form' ? 'Details' : 'Review'}
            </Text>
          </View>
        );
      })}
    </View>
  );

  const summaryStrip = !!form && (hasTurnaround(form.processingDays) || hasFee(form.fee)) && (
    <View style={styles.summaryStrip}>
      {hasTurnaround(form.processingDays) && (
        <View style={styles.summaryItem}>
          <FeatherIcon name="clock" size={14} color={colors.textSecondary} />
          <Text style={styles.summaryText}>{formatProcessingDays(form.processingDays)}</Text>
        </View>
      )}
      {hasFee(form.fee) && (
        <View style={styles.summaryItem}>
          <FeatherIcon name="tag" size={14} color={colors.textSecondary} />
          <Text style={styles.summaryText}>{formatFee(form.fee)}</Text>
        </View>
      )}
    </View>
  );

  const renderForm = () => {
    if (loading && !form) {
      return (
        <View style={styles.loader}>
          <LoadingSpinner message="Loading the form..." />
        </View>
      );
    }
    if (loadError) return <ErrorBanner message={loadError} onRetry={() => loadForm(false)} />;
    if (!form) return null;

    return (
      <View>
        {!!notice && <InfoBanner tone="warning" icon="refresh-cw" title="Form updated" message={notice} />}
        {summaryStrip}
        {!!form.description && <Text style={styles.description}>{form.description}</Text>}
        {!!form.parentInstructions && (
          <InfoBanner title="Before you start" message={form.parentInstructions} />
        )}

        {!eligible ? (
          <View style={[s.banner, { backgroundColor: colors.dangerLight, flexDirection: 'column' }]}>
            <View style={styles.inlineRow}>
              <FeatherIcon name="slash" size={16} color={colors.danger} />
              <Text style={s.bannerTitle}>Not available for {form.studentName}</Text>
            </View>
            {(form.eligibility.reasons.length > 0
              ? form.eligibility.reasons
              : ['The school has not opened this document for your child.']
            ).map(reason => (
              <Text key={reason} style={[s.bannerText, styles.reason]}>
                • {reason}
              </Text>
            ))}
          </View>
        ) : (
          <>
            {!!submitError && <ErrorBanner message={submitError} />}
            {form.fields.length === 0 ? (
              <Text style={s.helperText}>No details are needed. Continue to review and submit.</Text>
            ) : (
              <DocumentFieldList
                fields={form.fields}
                values={values}
                files={files}
                errors={errors}
                onValueChange={onValueChange}
                onFilePicked={onFilePicked}
              />
            )}
          </>
        )}
      </View>
    );
  };

  const renderReview = () => {
    if (!form) return null;
    const shown = form.fields.filter(field => isFieldVisible(field, values));
    return (
      <View>
        <Text style={s.sectionTitle}>Your answers</Text>
        <View style={styles.reviewCard}>
          {shown.length === 0 ? (
            <Text style={s.helperText}>No details were needed for this document.</Text>
          ) : (
            shown.map((field, index) => (
              <View
                key={field.key}
                style={[styles.reviewRow, index === shown.length - 1 && styles.reviewRowLast]}>
                <Text style={styles.reviewLabel}>{field.label}</Text>
                <Text style={styles.reviewValue}>
                  {displayAnswer(field, values[field.key], files[field.key])}
                </Text>
              </View>
            ))
          )}
        </View>

        <Text style={s.sectionTitle}>Summary</Text>
        <View style={styles.reviewCard}>
          <View style={styles.reviewRow}>
            <Text style={styles.reviewLabel}>Document</Text>
            <Text style={styles.reviewValue}>{form.name}</Text>
          </View>
          <View style={[styles.reviewRow, !hasFee(form.fee) && !hasTurnaround(form.processingDays) && styles.reviewRowLast]}>
            <Text style={styles.reviewLabel}>Child</Text>
            <Text style={styles.reviewValue}>
              {form.studentName}
              {form.className ? ` · ${form.className}` : ''}
            </Text>
          </View>
          {hasFee(form.fee) && (
            <View style={[styles.reviewRow, !hasTurnaround(form.processingDays) && styles.reviewRowLast]}>
              <Text style={styles.reviewLabel}>Fee</Text>
              <Text style={styles.reviewValue}>{formatFee(form.fee)}</Text>
            </View>
          )}
          {hasTurnaround(form.processingDays) && (
            <View style={[styles.reviewRow, styles.reviewRowLast]}>
              <Text style={styles.reviewLabel}>Expected turnaround</Text>
              <Text style={styles.reviewValue}>{formatProcessingDays(form.processingDays)} after approval</Text>
            </View>
          )}
        </View>

        <TouchableOpacity
          style={[styles.confirmRow, confirmError && !confirmed && styles.confirmRowError]}
          onPress={() => {
            setConfirmed(c => !c);
            setConfirmError(false);
          }}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: confirmed }}
          accessibilityLabel="I confirm the information above is correct">
          <FeatherIcon
            name={confirmed ? 'check-square' : 'square'}
            size={20}
            color={confirmed ? colors.primary : colors.textMuted}
          />
          <Text style={styles.confirmText}>
            I confirm the information above is correct and I am requesting this document for my child.
          </Text>
        </TouchableOpacity>
        {confirmError && !confirmed && (
          <Text style={s.errorText}>Please tick the confirmation to submit.</Text>
        )}
        {!!submitError && (
          <View style={{ marginTop: Spacing.md }}>
            <ErrorBanner message={submitError} />
          </View>
        )}
      </View>
    );
  };

  const renderDone = () => {
    if (!result) return null;
    return (
      <View style={styles.doneWrap}>
        <View style={styles.doneIcon}>
          <FeatherIcon name="check" size={34} color={colors.success} />
        </View>
        <Text style={styles.doneTitle}>The school has your request</Text>
        <Text style={styles.doneNumber} accessibilityLabel={`Request number ${result.requestNumber}`}>
          {result.requestNumber}
        </Text>
        <View style={{ alignSelf: 'center', marginTop: Spacing.sm }}>
          <StatusPill label={result.statusLabel} tone={result.statusTone} icon={requestStatusIcon(result.status)} />
        </View>
        <Text style={styles.doneText}>
          {result.documentName} for {result.studentName}.
          {result.expectedBy ? ` Expected by ${formatDocDate(result.expectedBy)}.` : ''} We will notify you
          as it moves along, and you can follow it under My Requests.
        </Text>
      </View>
    );
  };

  const footer = (() => {
    if (step === 'done') {
      return (
        <View style={styles.footer}>
          <TouchableOpacity style={s.bigSecondaryBtn} onPress={onClose} accessibilityRole="button">
            <Text style={s.bigSecondaryBtnText}>Done</Text>
          </TouchableOpacity>
          {!!result && (
            <TouchableOpacity
              style={s.bigPrimaryBtn}
              onPress={() => onViewRequest(result.id)}
              accessibilityRole="button">
              <Text style={s.bigPrimaryBtnText}>View request</Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }
    if (step === 'review') {
      return (
        <View style={styles.footer}>
          <TouchableOpacity
            style={s.bigSecondaryBtn}
            onPress={() => {
              setStep('form');
              setSubmitError(null);
            }}
            disabled={submitting}
            accessibilityRole="button">
            <Text style={s.bigSecondaryBtnText}>Back</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[s.bigPrimaryBtn, submitting && s.btnDisabled]}
            onPress={submit}
            disabled={submitting}
            accessibilityRole="button"
            accessibilityLabel="Submit request">
            {submitting ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={s.bigPrimaryBtnText}>Submit request</Text>
            )}
          </TouchableOpacity>
        </View>
      );
    }
    const canContinue = !!form && eligible && !loading;
    return (
      <View style={styles.footer}>
        <TouchableOpacity style={s.bigSecondaryBtn} onPress={onClose} accessibilityRole="button">
          <Text style={s.bigSecondaryBtnText}>{form && !eligible ? 'Close' : 'Cancel'}</Text>
        </TouchableOpacity>
        {(!form || eligible) && (
          <TouchableOpacity
            style={[s.bigPrimaryBtn, !canContinue && s.btnDisabled]}
            onPress={goToReview}
            disabled={!canContinue}
            accessibilityRole="button"
            accessibilityLabel="Continue to review">
            <Text style={s.bigPrimaryBtnText}>Review</Text>
            <FeatherIcon name="arrow-right" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        )}
      </View>
    );
  })();

  const body = (
    <>
      {header}
      {stepper}
      <ScrollView
        ref={scrollRef}
        style={styles.body}
        contentContainerStyle={styles.bodyContent}
        keyboardShouldPersistTaps="handled">
        {step === 'form' && renderForm()}
        {step === 'review' && renderReview()}
        {step === 'done' && renderDone()}
      </ScrollView>
      {footer}
    </>
  );

  return (
    <Modal
      visible={visible}
      transparent={isWide}
      animationType={isWide ? 'fade' : 'slide'}
      onRequestClose={() => {
        if (!submitting) onClose();
      }}>
      {isWide ? (
        <View style={s.modalOverlay}>
          <View style={styles.wideCard}>{body}</View>
        </View>
      ) : (
        <SafeAreaView style={styles.fullScreen} edges={['top', 'bottom']}>
          <KeyboardAvoidingView
            style={styles.flex}
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
            {body}
          </KeyboardAvoidingView>
        </SafeAreaView>
      )}
    </Modal>
  );
}

// ── Answering a correction ──────────────────────────────────────────────────

/**
 * Answering a correction: the earlier answers prefilled, only the fields the school
 * reopened editable, files replaceable, and an optional note for the office.
 */
export function ResubmitPanel({
  detail,
  onResubmitted,
}: {
  detail: ParentRequestDetail;
  onResubmitted: (updated: ParentRequestDetail) => void;
}) {
  const { colors, s } = useDocStyles();
  const fields = detail.resubmitFields ?? [];

  const { values, files, errors, setErrors, onValueChange, onFilePicked, reset } = useFieldState(
    initialFormValues(fields),
  );
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // A different request, or one whose correction changed, starts from its own answers.
  // Keyed on content rather than array identity so a quiet refresh keeps in-progress edits.
  const signature = useMemo(
    () => `${detail.id}|${fields.map(f => `${f.key}:${f.isEditable}:${f.currentValue ?? ''}`).join('|')}`,
    [detail.id, fields],
  );
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  useEffect(() => {
    reset(initialFormValues(fieldsRef.current));
    setNote('');
    setSubmitError(null);
  }, [signature, reset]);

  const existingFiles = useMemo(() => {
    const map: Record<string, string> = {};
    fields.forEach(field => {
      if (field.fieldType === 'FILE' && field.currentValue) map[field.key] = field.currentValue;
    });
    return map;
  }, [fields]);

  const editableCount = fields.filter(f => f.isEditable).length;

  const submit = async () => {
    if (submitting) return;
    const problems = validateDocumentForm(fields, values, files, {
      existingFileKeys: Object.keys(existingFiles),
    });
    setErrors(problems);
    setSubmitError(null);
    if (Object.keys(problems).length > 0) {
      setSubmitError(FIX_FIELDS_MESSAGE);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await documentsAPI.resubmit(detail.id, {
        values: buildSubmissionValues(fields, values),
        files: buildSubmissionFiles(fields, values, files),
        note,
      });
      onResubmitted(data);
    } catch (err) {
      Logger.error('Documents', 'Failed to resubmit document request', err);
      const problem = extractDocumentError(err, 'Could not send your changes. Please try again.');
      setErrors(mapServerFieldErrors(problem.fieldErrors, fields));
      setSubmitError(problem.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View>
      <Text style={s.intro}>
        {(detail.correction?.fieldKeys?.length ?? 0) > 0 && editableCount > 0
          ? 'Only the answers the school asked about can be changed. The rest stay as you sent them.'
          : 'Update your answers below and send the request back to the school.'}
      </Text>

      <DocumentFieldList
        fields={fields}
        values={values}
        files={files}
        errors={errors}
        existingFiles={existingFiles}
        onValueChange={onValueChange}
        onFilePicked={onFilePicked}
      />

      <Text style={s.label}>Note for the school (optional)</Text>
      <TextInput
        style={[s.input, s.textArea]}
        value={note}
        onChangeText={setNote}
        placeholder="Tell the office what you changed..."
        placeholderTextColor={colors.textMuted}
        multiline
        maxLength={NOTE_MAX}
        textAlignVertical="top"
        accessibilityLabel="Note for the school"
      />
      <Text style={[s.counter, { marginBottom: Spacing.md }]}>
        {note.length}/{NOTE_MAX}
      </Text>

      {!!submitError && <ErrorBanner message={submitError} />}

      <TouchableOpacity
        style={[s.bigPrimaryBtn, { flex: 0 }, submitting && s.btnDisabled]}
        onPress={submit}
        disabled={submitting}
        accessibilityRole="button"
        accessibilityLabel="Send changes to the school">
        {submitting ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <>
            <FeatherIcon name="send" size={16} color="#FFFFFF" />
            <Text style={s.bigPrimaryBtnText}>Send changes</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const createFieldStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    group: { marginBottom: Spacing.lg },
    labelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 },
    label: { flex: 1, fontSize: FontSize.sm, fontWeight: '600', color: colors.textPrimary },
    lockedTag: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: colors.divider,
    },
    lockedTagText: { fontSize: 10, fontWeight: '700', color: colors.textSecondary },
    textArea: { minHeight: 100 },
    readOnlyBox: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.sm,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      paddingVertical: 12,
      backgroundColor: colors.divider,
    },
    readOnlyText: { flex: 1, fontSize: FontSize.md, fontWeight: '600', color: colors.textPrimary },
    selectBox: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      paddingVertical: 13,
      backgroundColor: colors.background,
    },
    selectText: { flex: 1, fontSize: FontSize.md, color: colors.textPrimary },
    placeholderText: { color: colors.textMuted },
    radioRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      paddingVertical: 12,
      marginBottom: Spacing.sm,
    },
    radioRowActive: { borderColor: colors.primary, backgroundColor: colors.primaryLight },
    radioText: { flex: 1, fontSize: FontSize.md, color: colors.textPrimary, fontWeight: '500' },
    checkRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: Spacing.sm,
      paddingVertical: 4,
      borderRadius: BorderRadius.sm,
    },
    checkRowError: { borderWidth: 1.5, borderColor: colors.danger, padding: Spacing.sm },
    checkLabel: { flex: 1, fontSize: FontSize.md, color: colors.textPrimary, lineHeight: 20 },
    fileChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      paddingVertical: 10,
      backgroundColor: colors.background,
      marginBottom: Spacing.sm,
    },
    fileChipText: { flex: 1, minWidth: 0 },
    fileName: { fontSize: FontSize.md, fontWeight: '600', color: colors.textPrimary },
    fileMeta: { fontSize: FontSize.xs, color: colors.textSecondary, marginTop: 2 },
    fileButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: Spacing.sm,
      borderWidth: 1.5,
      borderStyle: 'dashed',
      borderColor: colors.primary,
      borderRadius: BorderRadius.sm,
      paddingVertical: 12,
      backgroundColor: colors.primaryLight,
    },
    fileButtonText: { fontSize: FontSize.md, fontWeight: '700', color: colors.primary },
  });

const createFlowStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    flex: { flex: 1 },
    fullScreen: { flex: 1, backgroundColor: colors.background },
    wideCard: {
      backgroundColor: colors.background,
      borderRadius: BorderRadius.md,
      width: '100%',
      maxWidth: 640,
      maxHeight: '92%',
      overflow: 'hidden',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: Spacing.sm,
      paddingHorizontal: Spacing.md,
      paddingTop: Spacing.md,
      paddingBottom: Spacing.sm,
      backgroundColor: colors.surface,
    },
    headerText: { flex: 1 },
    headerTitle: { fontSize: FontSize.xl, fontWeight: '800', color: colors.textPrimary },
    headerSubtitle: { fontSize: FontSize.sm, color: colors.textSecondary, marginTop: 2 },
    closeBtn: { padding: 4 },
    stepper: {
      flexDirection: 'row',
      gap: Spacing.lg,
      paddingHorizontal: Spacing.md,
      paddingBottom: Spacing.sm,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    stepItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    stepDot: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    stepDotActive: { backgroundColor: colors.primary, borderColor: colors.primary },
    stepNum: { fontSize: 11, fontWeight: '700', color: colors.textMuted },
    stepNumActive: { color: '#FFFFFF' },
    stepLabel: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textMuted },
    stepLabelActive: { color: colors.primary },
    body: { flex: 1 },
    bodyContent: { padding: Spacing.md, paddingBottom: Spacing.xl },
    loader: { alignItems: 'center', paddingVertical: 48 },
    summaryStrip: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: Spacing.md,
      backgroundColor: colors.card,
      borderRadius: BorderRadius.sm,
      padding: Spacing.md,
      marginBottom: Spacing.md,
    },
    summaryItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    summaryText: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textPrimary },
    description: {
      fontSize: FontSize.md,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: Spacing.md,
    },
    inlineRow: { flexDirection: 'row', alignItems: 'center', gap: Spacing.sm, marginBottom: 4 },
    reason: { marginTop: 4 },
    reviewCard: {
      backgroundColor: colors.card,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      marginBottom: Spacing.lg,
      borderWidth: 1,
      borderColor: colors.divider,
    },
    reviewRow: {
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.divider,
    },
    reviewRowLast: { borderBottomWidth: 0 },
    reviewLabel: { fontSize: FontSize.sm, color: colors.textSecondary, fontWeight: '600' },
    reviewValue: { fontSize: FontSize.md, color: colors.textPrimary, marginTop: 3, lineHeight: 20 },
    confirmRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: Spacing.sm,
      backgroundColor: colors.card,
      borderRadius: BorderRadius.sm,
      padding: Spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    confirmRowError: { borderColor: colors.danger, borderWidth: 1.5 },
    confirmText: { flex: 1, fontSize: FontSize.md, color: colors.textPrimary, lineHeight: 20 },
    footer: {
      flexDirection: 'row',
      gap: Spacing.sm,
      padding: Spacing.md,
      backgroundColor: colors.surface,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    doneWrap: { alignItems: 'stretch', paddingVertical: Spacing.lg },
    doneIcon: {
      alignSelf: 'center',
      width: 72,
      height: 72,
      borderRadius: 36,
      backgroundColor: colors.successLight,
      alignItems: 'center',
      justifyContent: 'center',
    },
    doneTitle: {
      fontSize: FontSize.xl,
      fontWeight: '800',
      color: colors.textPrimary,
      textAlign: 'center',
      marginTop: Spacing.md,
    },
    doneNumber: {
      fontSize: FontSize.xxl,
      fontWeight: '800',
      color: colors.primary,
      textAlign: 'center',
      marginTop: Spacing.sm,
      letterSpacing: 0.5,
    },
    doneText: {
      fontSize: FontSize.md,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 21,
      marginTop: Spacing.md,
    },
  });
